/**
 * Select — dropdown component dùng chung cho admin.
 *
 * Tự động ẩn ô tìm kiếm khi options ít (< searchThreshold).
 * Style match input INP của builder (rounded-xl, py-2.5, border-gray-300).
 *
 * Props:
 *  - options: [{ value, label, icon?, group?, disabled?, hint? }]
 *  - value: any (single value)
 *  - onChange: (newValue) => void
 *  - placeholder: string
 *  - disabled: bool
 *  - clearable: bool — mặc định true (chỉ khi có value)
 *  - searchable: 'auto' | true | false — mặc định 'auto'
 *  - searchThreshold: number — default 8 (>= mới show search ở 'auto')
 *  - size: 'sm' | 'md' — default 'md'
 *  - className: extra classes for trigger
 *  - emptyText: hiển thị khi không có options match
 *  - renderOption: (option, { selected }) => ReactNode — custom render row
 *  - renderValue: (option) => ReactNode — custom render trigger label
 *  - portal: bool — dùng React portal cho menu (default true) — tránh overflow clipping
 */
import { useState, useRef, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, Search, X, Check } from 'lucide-react';

import { useLingui } from '@lingui/react/macro';
import { tOptions, i18n } from '../../i18n/runtime.js';
import { msg } from '@lingui/core/macro';

const SIZES = {
  sm: { trigger: 'px-2.5 py-1.5 text-xs rounded-lg', menu: 'text-xs' },
  md: { trigger: 'px-3 py-2.5 text-sm rounded-xl', menu: 'text-sm' },
};

export default function Select({
  options: optionsRaw = [],
  value = '',
  onChange,
  placeholder = i18n._(msg`Chọn...`),
  disabled = false,
  clearable = true,
  searchable = 'auto',
  searchThreshold = 8,
  size = 'md',
  className = '',
  emptyText = i18n._(msg`Không có lựa chọn`),
  renderOption,
  renderValue,
  portal = true,
}) {
  const {
    t
  } = useLingui();

  // 🛡️ Hàng rào descriptor: bảng nhãn hay được ném nguyên cụm vào đây qua prop
  // (`options={KIND_OPTIONS}`). Dịch NGAY Ở ĐÂY để chỗ gọi không phải nhớ — xem tOptions.
  const options = useMemo(() => tOptions(optionsRaw), [optionsRaw]);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  const sz = SIZES[size] || SIZES.md;
  const showSearch = searchable === true || (searchable === 'auto' && options.length >= searchThreshold);

  const filteredOptions = useMemo(() => {
    if (!showSearch || !search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter(o =>
      String(o.label || '').toLowerCase().includes(q) ||
      String(o.value || '').toLowerCase().includes(q)
    );
  }, [options, search, showSearch]);

  // Group options if `group` field present
  const groupedOptions = useMemo(() => {
    const hasGroup = filteredOptions.some(o => o.group);
    if (!hasGroup) return [{ group: null, items: filteredOptions }];
    const map = new Map();
    filteredOptions.forEach(o => {
      const key = o.group || '';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(o);
    });
    return [...map.entries()].map(([group, items]) => ({ group, items }));
  }, [filteredOptions]);

  const selectedOption = options.find(o => o.value === value);

  // Position menu relative to trigger when portal mode
  useLayoutEffect(() => {
    if (!open || !portal || !triggerRef.current) return;
    const compute = () => {
      const r = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const menuMaxHeight = 320;
      const placeAbove = spaceBelow < menuMaxHeight && r.top > spaceBelow;
      setMenuPos({
        left: r.left,
        top: placeAbove ? r.top - 4 : r.bottom + 4,
        width: r.width,
        placeAbove,
      });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open, portal]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false); setSearch(''); setHighlightIdx(-1);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus search when opened
  useEffect(() => {
    if (open && showSearch) setTimeout(() => searchRef.current?.focus(), 0);
  }, [open, showSearch]);

  // Reset highlight when filter changes
  useEffect(() => {
    if (open) setHighlightIdx(filteredOptions.length > 0 ? 0 : -1);
  }, [open, search]);

  const flatList = useMemo(() => groupedOptions.flatMap(g => g.items), [groupedOptions]);

  const onSelect = useCallback((opt) => {
    if (opt.disabled) return;
    onChange?.(opt.value);
    setOpen(false); setSearch(''); setHighlightIdx(-1);
  }, [onChange]);

  const onClear = useCallback((e) => {
    e.stopPropagation();
    onChange?.('');
  }, [onChange]);

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault(); setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); setOpen(false); setSearch(''); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIdx(i => Math.min(flatList.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIdx(i => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (flatList[highlightIdx]) onSelect(flatList[highlightIdx]); }
  };

  // Trigger
  const trigger = (
    <button type="button" ref={triggerRef} disabled={disabled}
      onClick={() => !disabled && setOpen(o => !o)} onKeyDown={onKeyDown}
      className={`group relative w-full flex items-center justify-between gap-2 border bg-white transition focus:outline-none ${sz.trigger}
        ${disabled ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed' :
          open ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-300 hover:border-gray-400'}
        ${className}`}>
      <span className={`flex-1 min-w-0 text-left truncate ${selectedOption ? 'text-gray-900' : 'text-gray-400'}`}>
        {selectedOption
          ? (renderValue ? renderValue(selectedOption) : (
              <span className="flex items-center gap-1.5">
                {selectedOption.icon && <selectedOption.icon size={size === 'sm' ? 12 : 14} className="shrink-0" />}
                <span className="truncate">{selectedOption.label}</span>
              </span>
            ))
          : placeholder}
      </span>
      <span className="flex items-center gap-1 shrink-0">
        {clearable && selectedOption && !disabled && (
          <span onClick={onClear} role="button" tabIndex={-1}
            className="p-0.5 text-gray-300 hover:text-gray-600 hover:bg-gray-100 rounded transition opacity-0 group-hover:opacity-100">
            <X size={size === 'sm' ? 11 : 13} />
          </span>
        )}
        <ChevronDown size={size === 'sm' ? 13 : 15}
          className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </span>
    </button>
  );

  // Menu
  const menu = open && (
    <div ref={menuRef}
      className={`bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden ${sz.menu} ${portal ? 'fixed' : 'absolute left-0 right-0 mt-1'}`}
      style={portal && menuPos ? {
        left: menuPos.left, width: menuPos.width,
        ...(menuPos.placeAbove ? { bottom: window.innerHeight - menuPos.top, top: 'auto' } : { top: menuPos.top }),
        zIndex: 10000,
      } : undefined}>

      {showSearch && (
        <div className="px-2.5 py-2 border-b border-gray-100">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input ref={searchRef} value={search} onChange={e => setSearch(e.target.value)} onKeyDown={onKeyDown}
              placeholder={t`Tìm kiếm...`}
              className="w-full pl-7 pr-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none" />
          </div>
        </div>
      )}

      <div className="max-h-72 overflow-y-auto py-1">
        {flatList.length === 0 ? (
          <p className="text-center py-4 text-gray-400 text-xs">{emptyText}</p>
        ) : (
          groupedOptions.map((g, gi) => (
            <div key={gi}>
              {g.group && (
                <p className="px-3 pt-2 pb-1 text-[10px] font-bold text-gray-400 uppercase tracking-wider">{g.group}</p>
              )}
              {g.items.map((opt) => {
                const isSelected = opt.value === value;
                const flatIdx = flatList.findIndex(x => x.value === opt.value);
                const isHighlight = flatIdx === highlightIdx;
                const Icon = opt.icon;
                return (
                  <button key={opt.value} type="button"
                    onClick={() => onSelect(opt)}
                    onMouseEnter={() => setHighlightIdx(flatIdx)}
                    disabled={opt.disabled}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left transition ${
                      opt.disabled ? 'text-gray-300 cursor-not-allowed' :
                      isSelected ? 'bg-blue-50 text-blue-700 font-medium' :
                      isHighlight ? 'bg-gray-50 text-gray-900' : 'text-gray-700 hover:bg-gray-50'
                    }`}>
                    {Icon && <Icon size={14} className="shrink-0" />}
                    {renderOption ? renderOption(opt, { selected: isSelected }) : (
                      <span className="flex-1 min-w-0 truncate">{opt.label}</span>
                    )}
                    {opt.hint && <span className="text-[10px] text-gray-400 ml-auto">{opt.hint}</span>}
                    {isSelected && <Check size={13} className="shrink-0 text-blue-600" />}
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="relative">
      {trigger}
      {!portal && menu}
      {portal && menu && ReactDOM.createPortal(menu, document.body)}
    </div>
  );
}
